import { notify } from "@/components/common/Notification";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import { forwardRef, useEffect, useImperativeHandle } from "react";

const SchemaFormAutoSubmit = forwardRef(
  (
    {
      form,
      schemaName,
      recordId,
    }: {
      form: any;
      schemaName?: string;
      recordId?: string;
    },
    ref
  ) => {
    const { data: schemaJson } = useQuery({
      queryKey: ["schema", schemaName],
      queryFn: () => SchemaService.getSchemaByName(schemaName ?? ""),
      enabled: !!schemaName,
    });
    const isEditMode = !!recordId;

    const { data: recordInfo } = useQuery({
      queryKey: ["dynamic-model", schemaName, recordId],
      queryFn: () =>
        DynamicModuleService.getItemById(schemaName ?? "", recordId ?? ""),
      enabled: isEditMode && !!schemaName && !!recordId,
    });

    const handleSave = async () => {
      await form.validate();
      const values = removeMetaFields(form.values);
      if (isEditMode) {
        // Check for changes before updating
        if (!checkFormChanges(values, recordInfo, notify)) {
          return;
        }
        await DynamicModuleService.updateItem(
          schemaName ?? "",
          recordId ?? "",
          values
        );
      } else {
        await DynamicModuleService.createItem(schemaName ?? "", values);
      }
    };

    useImperativeHandle(ref, () => ({
      handleSave,
    }));

    useEffect(() => {
      if (isEditMode && recordInfo) {
        form.setInitialValues(removeMetaFields(recordInfo));
      }
    }, [isEditMode, recordInfo, form]);

    return (
      <SchemaFormRender
        form={form}
        schemaJson={schemaJson?.json ?? schemaJson}
      />
    );
  }
);

export default SchemaFormAutoSubmit;
